import Nivo from "@/components/Nivo/Nivo";
import Column from "@/components/Ui/Column";
import Spinner from "@/components/Ui/Spinner";
import GraphLoadingWrapper from "@/components/portal/graph/GraphLoadingWrapper";
import { STATS_API } from "@/constants/env";
import { useSelectedDatesContext } from "@/contexts/selected-dates-context";
import { useVendorContext } from "@/contexts/vendor-context";
import { useOnScreen } from "@/hooks/use-on-screen";
import useSesamyAuthFetch from "@/hooks/use-sesamy-auth-fetch";
import { GraphToggleOption, StatsResponse } from "@/utils/graphHelpers";
import { useEffect, useRef, useState } from "react";
import useSWR from "swr";

type Props = {
  endPoint: string;
  gradient?: boolean;
  toggleOptions?: GraphToggleOption[];
  equalHeight?: boolean;
  extraQueryParams?: [string, string][];
};

const GraphLoader = ({
  endPoint,
  gradient = false,
  toggleOptions: initialToggleOptions,
  equalHeight = false,
  extraQueryParams = [],
}: Props) => {
  const elementRef = useRef(null);
  const isOnScreen = useOnScreen(elementRef);
  const renderedState = useState(false);
  const [rendered, setRendered] = renderedState;

  const { selectedVendor } = useVendorContext();
  const { formattedDates } = useSelectedDatesContext();
  const { startDate, endDate } = formattedDates;
  const { fetcher, isReady } = useSesamyAuthFetch();

  const [toggleOptions, setToggleOptions] = useState<
    GraphToggleOption[] | undefined
  >(initialToggleOptions);
  const [currentEndpoint, setCurrentEndpoint] = useState(endPoint);
  useEffect(() => {
    setCurrentEndpoint(`${endPoint}`);
  }, [endPoint]);

  // Build extra query parameters string
  const buildExtraQueryString = (params: [string, string][]) => {
    if (params.length === 0) return "";
    return (
      "&" +
      params
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
        )
        .join("&")
    );
  };

  const { data, isLoading } = useSWR<StatsResponse>(
    isReady &&
      `${STATS_API}/${currentEndpoint}${currentEndpoint.includes("?") ? "&" : "?"}vendorId=${selectedVendor}&fromDate=${startDate}&toDate=${endDate}${buildExtraQueryString(extraQueryParams)}`,
    fetcher,
  );

  const onGraphToggle = (option: GraphToggleOption) => {
    setRendered(false);
    setCurrentEndpoint(option.endpoint);
    setToggleOptions((prevOptions) =>
      prevOptions?.map((opt) =>
        opt.label === option.label
          ? { ...opt, current: true }
          : { ...opt, current: false },
      ),
    );
  };

  return (
    <GraphLoadingWrapper elementRef={elementRef} rendered={rendered}>
      {((isOnScreen && !isLoading) || rendered) && (
        <Nivo
          statsResponse={data}
          singleDate={startDate === endDate}
          startDate={new Date(startDate)}
          endDate={new Date(endDate)}
          isLegacyGraph={false}
          gradient={gradient}
          renderedState={renderedState}
          equalHeight={equalHeight}
          toggleOptions={toggleOptions}
          onGraphToggle={onGraphToggle}
        />
      )}

      {(isLoading || !rendered) && (
        <Column className="text-muted-foreground absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] gap-2">
          {!isOnScreen
            ? "Loading..."
            : isLoading
              ? "Fetching data.."
              : !rendered
                ? "Rendering graph..."
                : null}

          <Spinner className="text-3xl" />
        </Column>
      )}
    </GraphLoadingWrapper>
  );
};

export default GraphLoader;
